import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { Search } from "components/DesignSystem/Search/Search";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { DropDownItem } from "components/GeneralLedger/GeneralLedgerFilters";
import { Cross } from "components/icons/Cross";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useState } from "react";
import { HiddenCategoryTypes } from "store/apis/chartOfAccounts";
import { flattenTypes } from "utils/flattenCOA";
import { stopPropagation } from "utils/stopPropagation";

type CategoryMultiSelectProps = {
  selectedUuids: string[];
  onChange: (uuids: string[]) => void;
  hiddenCategoryTypes?: HiddenCategoryTypes[];
  rootCategoryId?: string;
};

export const CategoryMultiSelect = ({
  selectedUuids,
  onChange,
  hiddenCategoryTypes,
  rootCategoryId,
}: CategoryMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearch("");
  };

  const {
    chartOfAccounts,
    isLoading,
    isFetching,
    isSuccess: isCategoriesSuccess,
  } = useChartOfAccounts({
    search,
    hiddenCategoryTypes,
    root_category_id: rootCategoryId,
  });

  const allCategories = flattenTypes({ accounts: chartOfAccounts });
  const noCategoriesFound = chartOfAccounts.length === 0;
  const isSelectedLabelsLoading = isFetching || !isCategoriesSuccess;

  const getCategoryLabel = (uuid: string) =>
    allCategories?.find((category) => category.uuid === uuid)?.name || uuid;

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;

    if (checked) {
      onChange([...selectedUuids, value]);
      return;
    }

    onChange(selectedUuids.filter((uuid) => uuid !== value));
  };

  const handleRemove = (uuid: string) => {
    onChange(selectedUuids.filter((selectedUuid) => selectedUuid !== uuid));
  };

  return (
    <Dropdown.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dropdown.Trigger asChild>
        <div className="t-flex t-cursor-pointer t-flex-wrap t-items-center t-gap-1.5">
          {isSelectedLabelsLoading &&
            selectedUuids.map((uuid) => (
              <Skeleton key={uuid} className="t-h-6 t-w-20" />
            ))}

          {!isSelectedLabelsLoading &&
            selectedUuids.map((uuid) => (
              <Tag key={uuid} tagType="dark_gray" icon={false} size="small">
                <div className="t-flex t-items-center t-gap-1">
                  <span>{getCategoryLabel(uuid)}</span>
                  <button
                    type="button"
                    className="all:unset t-flex-shrink-0 t-cursor-pointer t-rounded-md t-px-0.5 hover:t-bg-neutral-20"
                    onPointerDown={stopPropagation}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(uuid);
                    }}
                  >
                    <Cross size="12" />
                  </button>
                </div>
              </Tag>
            ))}

          <Button
            size="small"
            customType="link"
            onClick={(e) => e.stopPropagation()}
          >
            + Add
          </Button>
        </div>
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content sideOffset={6} align="start" className="t-w-[430px]">
          <Search
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            customSize="regular"
            block
          />
          <Async.Root
            isLoading={isLoading}
            isSuccess={isCategoriesSuccess}
            isEmpty={noCategoriesFound}
            customLoader={
              <div className="t-my-1.5 t-flex t-h-48 t-items-center t-justify-center t-text-text-30">
                Loading...
              </div>
            }
          >
            <Async.Empty>
              <div className="t-my-1.5 t-flex t-h-48 t-items-center t-justify-center t-text-text-30">
                No categories found
              </div>
            </Async.Empty>

            <Async.Success>
              <div className="t-mt-2 t-max-h-72 t-overflow-auto">
                {chartOfAccounts.map((account) => (
                  <div key={account.uuid}>
                    <Dropdown.Label className="t-bg-neutral-0 !t-text-text-60">
                      {account.identifier}-{account.name}
                    </Dropdown.Label>
                    <DropDownItem
                      types={account.types}
                      level={0}
                      onHandleChange={handleToggle}
                      categoryIds={selectedUuids}
                    />
                  </div>
                ))}
              </div>
            </Async.Success>
          </Async.Root>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};
